<template>
  <div>

    <div class="card">
      <div class="card-body">
        <div class="row mb-3">
          <div class="col-lg-4 mb-2 mb-lg-0">
            <b-input-group>
              <b-form-input
                v-model.trim="searchText"
                type="search"
                :placeholder="$t('label.search')"
                @keyup.enter="applySearch"
              />
              <b-input-group-append
                ><b-button variant="info" @click="applySearch"
                  ><i class="mdi mdi-magnify"></i></b-button
              ></b-input-group-append>
            </b-input-group>
          </div>
          <div class="col-lg-8 d-flex justify-content-lg-end">
            <b-dropdown
              ref="orderFilters"
              variant="warning"
              no-caret
              right
              boundary="viewport"
              menu-class="order-filter-menu"
              @show="openFilters"
              @hidden="closeFilters"
            >
              <template #button-content
                ><i class="mdi mdi-filter"></i
              ></template>
              <form class="px-3 py-2" @submit.prevent="applyFilters">
                <b-form-group
                  label-class="small text-muted"
                  :label="$t('label.orderDateFrom')"
                >
                  <b-form-input
                    v-model="filterDraft.dateFrom"
                    type="date"
                    :max="filterDraft.dateTo || null"
                    required
                  />
                </b-form-group>
                <b-form-group
                  label-class="small text-muted"
                  :label="$t('label.orderDateTo')"
                >
                  <b-form-input
                    v-model="filterDraft.dateTo"
                    type="date"
                    :min="filterDraft.dateFrom || null"
                    required
                  />
                </b-form-group>
                <b-form-group
                  label-class="small text-muted"
                  :label="$t('label.status')"
                >
                  <multiselect
                    v-model="draftStatus"
                    :options="statusOptions"
                    track-by="value"
                    label="text"
                    :placeholder="$t('label.orderAllStatuses')"
                    :allow-empty="true"
                    :internal-search="false"
                    :loading="statusLoading"
                    @search-change="searchStatuses"
                  />
                  <b-form-text v-if="statusLookupError" class="text-danger">
                    {{ $t("label.orderStatusLookupError") }}
                    <b-button
                      type="button"
                      size="sm"
                      variant="outline-danger"
                      @click="loadStatuses()"
                      >{{ $t("label.retryPermissionDefinitions") }}</b-button
                    >
                  </b-form-text>
                </b-form-group>
                <b-alert v-if="filterError" show variant="danger">{{
                  filterError
                }}</b-alert>
                <div class="d-flex justify-content-end">
                  <b-button
                    type="button"
                    size="sm"
                    variant="outline-secondary"
                    @click="resetFilters"
                    >{{ $t("label.reset") }}</b-button
                  >
                  <b-button
                    type="submit"
                    size="sm"
                    variant="info"
                    class="ml-2"
                    >{{ $t("label.search") }}</b-button
                  >
                </div>
              </form>
            </b-dropdown>
            <b-button variant="info" class="ml-2" @click="openForm()">
              <i class="mdi mdi-plus mr-1"></i
              >{{ $t("label.createX", [$t("module.order")]) }}
            </b-button>
          </div>
        </div>
        <div class="text-muted small mb-3">
          {{ searchCriteria.dateFrom }} — {{ searchCriteria.dateTo }}
          <b-badge v-if="activeStatus" variant="info" class="ml-2">{{
            activeStatus.text
          }}</b-badge>
        </div>
        <div v-if="!status.list" class="d-flex justify-content-center p-5">
          <b-spinner variant="info" />
        </div>
        <b-alert v-else-if="listError" show variant="danger">
          {{ $t("label.orderLoadError") }}
          <b-button size="sm" variant="outline-danger" @click="loadOrders">{{
            $t("label.retryPermissionDefinitions")
          }}</b-button>
        </b-alert>
        <template v-else>
          <b-table :items="tableRows" :fields="fields" show-empty responsive>
            <template #cell(totalAmount)="{ item }">{{
              Number(item.totalAmount).toFixed(2)
            }}</template>
            <template #cell(statusName)="{ item }"
              ><b-badge variant="info">{{
                item.statusName || "-"
              }}</b-badge></template
            >
            <template #cell(actions)="{ item }">
              <a
                href="javascript:void(0);"
                class="text-info"
                @click.stop="openForm(item)"
                ><i
                  :class="[
                    'mdi',
                    item.statusName === 'New'
                      ? 'mdi-pencil-outline'
                      : 'mdi-eye-outline',
                    'font-size-18',
                  ]"
                ></i
              ></a>
              <a
                href="javascript:void(0);"
                class="text-danger ml-2"
                @click.stop="confirmDelete(item)"
                ><i class="mdi mdi-delete-outline font-size-18"></i
              ></a>
            </template>
          </b-table>
          <div
            v-if="pagination.total"
            class="d-flex justify-content-between align-items-center mt-3"
          >
            <small class="text-muted">{{
              $tc("label.results", pagination.total, [pagination.total])
            }}</small>
            <b-pagination
              v-model="currentPage"
              :total-rows="pagination.total"
              :per-page="searchCriteria.pageSize"
              class="mb-0"
              @change="pageNavigation"
            />
          </div>
        </template>
      </div>
    </div>
    <b-modal
      id="order-form"
      :title="
        $t(order.uuid ? 'label.updateX' : 'label.createX', [$t('module.order')])
      "
      size="xl"
      hide-footer
      hide-header-close
      centered
      @hidden="closeForm"
      ><b-container fluid>
        <OrderForm
          v-if="formVisible"
          :order-u-u-i-d="order.uuid"
          :search-criteria="searchCriteria"
          @done="closeForm"
        /> </b-container
    ></b-modal>
  </div>
</template>

<script>
import { mapState } from "vuex";
import i18n from "@/i18n";
import { orderMethods } from "@/state/api";
import { ORDER_MODULE } from "@/state/api/order.state";
import OrderForm from "@/views/pages/admin/order/form";
import Multiselect from "vue-multiselect";
import { lookupService } from "@/services/api/lookup.service";
import { defaultOrderDateRange } from "@/utils/order-date-range.helper";

const defaultCriteria = () => ({
  pageNumber: 1,
  pageSize: 15,
  searchArray: [],
  statusUUID: "",
  ...defaultOrderDateRange(),
});

export default {
  name: "OrderListing",
  
  components: { OrderForm, Multiselect },
  data() {
    return {
      filterDraft: defaultOrderDateRange(),
      filterError: "",
      draftStatus: null,
      activeStatus: null,
      statusOptions: [],
      statusLoading: false,
      statusLookupError: false,
      statusRequest: 0,
      statusTimer: null,
      searchText: "",
      currentPage: 1,
      formVisible: false,
      order: { uuid: "" },
      searchCriteria: defaultCriteria(),
      breadcrumbs: [
        { text: i18n.t("module.marketplace"), href: "/marketplaces" },
        { text: i18n.t("module.order"), active: true },
      ],
      fields: [
        { key: "orderNo", label: i18n.t("label.orderNo") },
        { key: "buyerName", label: i18n.t("label.orderBuyer") },
        { key: "buyerEmail", label: i18n.t("label.email") },
        { key: "totalAmount", label: i18n.t("label.totalAmount") },
        { key: "statusName", label: i18n.t("label.status") },
        { key: "createdAt", label: i18n.t("label.createdAt") },
        { key: "actions", label: i18n.t("label.action") },
      ],
    };
  },
  computed: {
    ...mapState(ORDER_MODULE, ["status", "listError"]),
    tableRows() {
      return this.$store.state.api.order.list.data || [];
    },
    pagination() {
      return this.$store.state.api.order.list.pagination || {};
    },
  },
  created() {
    this.loadOrders();
  },
  beforeDestroy() {
    clearTimeout(this.statusTimer);
    this.statusRequest++;
  },
  methods: {
    ...orderMethods,
    closeFilters() {
      clearTimeout(this.statusTimer);
      this.statusRequest++;
      this.statusLoading = false;
    },
    openFilters() {
      clearTimeout(this.statusTimer);
      this.filterDraft = {
        dateFrom: this.searchCriteria.dateFrom,
        dateTo: this.searchCriteria.dateTo,
      };
      this.draftStatus = this.activeStatus;
      this.filterError = "";
      this.loadStatuses();
    },
    resetFilters() {
      this.filterDraft = defaultOrderDateRange();
      this.draftStatus = null;
      this.filterError = "";
    },
    applyFilters() {
      const { dateFrom, dateTo } = this.filterDraft;
      if (
        !/^\d{4}-\d{2}-\d{2}$/.test(dateFrom || "") ||
        !/^\d{4}-\d{2}-\d{2}$/.test(dateTo || "") ||
        dateFrom > dateTo
      ) {
        this.filterError = this.$t("label.orderDateRangeInvalid");
        return;
      }
      this.currentPage = 1;
      this.activeStatus = this.draftStatus;
      this.searchCriteria = {
        ...this.searchCriteria,
        pageNumber: 1,
        dateFrom,
        dateTo,
        statusUUID: this.draftStatus?.value || "",
      };
      this.$refs.orderFilters.hide(true);
      this.loadOrders();
    },
    async loadStatuses(term = "") {
      const request = ++this.statusRequest;
      this.statusLoading = true;
      this.statusLookupError = false;
      try {
        const result = await lookupService.getOrderStatuses(term ? [term] : []);
        if (request !== this.statusRequest) return;
        const data = result?.data?.data || result?.data || result;

        if (result?.success === false || !Array.isArray(data))
          throw new Error("Status lookup failed.");
        this.statusOptions = data.map((item) => ({
          value: item.uuid,
          text: item.name,
        }));
      } catch (error) {
        if (request === this.statusRequest) {
          this.statusLookupError = true;
        }
      } finally {
        if (request === this.statusRequest) {
          this.statusLoading = false;
        }
      }
    },
    searchStatuses(term) {
      clearTimeout(this.statusTimer);
      this.statusRequest++;
      this.statusTimer = setTimeout(() => this.loadStatuses(term), 350);
    },
    loadOrders() {
      return this.getAllOrder({ filter: this.searchCriteria });
    },
    applySearch() {
      this.currentPage = 1;
      this.searchCriteria = {
        ...this.searchCriteria,
        pageNumber: 1,
        searchArray: this.searchText ? [this.searchText] : [],
      };
      this.loadOrders();
    },
    pageNavigation(pageNumber) {
      this.currentPage = pageNumber;
      this.searchCriteria = { ...this.searchCriteria, pageNumber };
      this.loadOrders();
    },
    openForm(order = null) {
      this.order = { uuid: order?.uuid || "" };
      this.formVisible = true;
      this.$nextTick(() => this.$bvModal.show("order-form"));
    },
    closeForm() {
      this.$bvModal.hide("order-form");
      this.formVisible = false;
      this.order = { uuid: "" };
    },
    async confirmDelete(order) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        this.$t("label.confirmDeleteX", [this.$t("module.order")]),
        {
          title: this.$t("label.delete"),
          okTitle: this.$t("label.delete"),
          okVariant: "danger",
          cancelTitle: this.$t("label.cancel"),
          centered: true,
        },
      );
      if (confirmed) {
        const result = await this.deleteOrder({
          uuid: order.uuid,
          filter: this.searchCriteria,
        });
        if (result?.success && this.currentPage > 1 && !this.tableRows.length) {
          this.pageNavigation(this.currentPage - 1);
        }
      }
    },
  },
};
</script>

<style scoped>
::v-deep .order-filter-menu {
  min-width: 300px;
  max-width: calc(100vw - 2rem);
}
</style>
